import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  ViewChild
}                                     from '@angular/core';
import {Observable, of, Subscription}     from 'rxjs';
import {Validators}                   from '@angular/forms';
import {ApMenuUrls}                   from '../../ap-interface';
import {CldrIntlService, IntlService} from '@progress/kendo-angular-intl';
import {
  ApDynformsConfigFieldset
}                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox,
  ApDynformsConfigTextboxType
}                                     from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsValidator}          from '../../ap-dynforms/ap-dynforms-validator';
import {ApDynformsComponent}          from '../../ap-dynforms/ap-dynforms.component';
import {LabelSettings}                from '@progress/kendo-angular-progressbar';
import {Trace}                        from '../../debug-utils/ApplicationTracer';
import {LoginStore}                   from '../../stores/login/login.store';
import {LanguageStore}                from '../../stores/translation/language.store';
import {ActiveUsersStore}             from '../../stores/administration/active-users.store';

/**
 * Component for the login
 */
@Component({
  selector: 'ap-login-form',
  templateUrl: 'ap-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApLoginComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  private emailFormKey = 'email';
  private passwordFormKey = 'password';
  private _subscriptions: Subscription[] = [];
  public loginLoading$: Observable<boolean>;
  public loginErrorMessage$: Observable<string>;
  public loginDelay$: Observable<number>;
  public actualProgress$: Observable<number>;
  public maxProgress$: Observable<number>;
  public showProgressBar$: Observable<boolean> = of(false);
  public ApMenuUrls = ApMenuUrls;
  public formConfig: ApDynformsConfigFieldset[];
  public caption: string;
  public progressBarLabel: LabelSettings = {
    visible: false,
    format: 'percent',
    position: 'end'
  };

  constructor(private loginStore: LoginStore,
              private languageStore: LanguageStore,
              private activeUsersStore: ActiveUsersStore,
              @Inject(LOCALE_ID) public localeId,
              public intlService: IntlService,
              public changeDetectorRef: ChangeDetectorRef) {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'login_fieldset',
        columns: 1,
        legend: 'Tip_LoginText',
        label: 'Global_Login_Email_Password',
        asyncTranslation: true,
        config: [
          new ApDynformsConfigTextbox({
            key: this.emailFormKey,
            label: 'Lbl_Mail',
            value: '',
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Global_Login_Enter_Email'
              }),
              new ApDynformsValidator({
                validator: Validators.pattern('^[-_.\\w]+@([0-9a-zA-Z][-\\w]*[0-9a-zA-Z]\\.){1,300}[a-zA-Z]{2,9}$'),
                errorKey: 'Admin_Pages_Login_Reset_Password_Email_Error'
              }),
              new ApDynformsValidator({
                validator: Validators.email,
                errorKey: 'Global_Login_Enter_Email'
              })]
          }),
          new ApDynformsConfigTextbox({
            key: this.passwordFormKey,
            label: 'Lbl_Password',
            value: '',
            type: ApDynformsConfigTextboxType.Password,
            validators: [new ApDynformsValidator({
              validator: Validators.required,
              errorKey: 'Admin_Pages_Login_Error_Password'
            })]
          })
        ]
      })
    ];
  }

  /**
   * when the component is loaded
   */
  @Trace()
  ngAfterContentInit(): void {
    this.loginStore.updateLoadingProgress(0);
    this.getDataFromStore();
    this.activateListeners();
  }

  activateListeners(): void {
    // reload the Localization of the Kendo Language Service
    // used for DatePicker
    this._subscriptions.push(this.languageStore.Listen(s => s.selectedLanguage)
      .subscribe((l) => {
        if (!l) {
          return;
        }
        this.localeId = l.Key;
        (this.intlService as CldrIntlService).localeId = l.Key;
      }));

    this._subscriptions.push(this.loginErrorMessage$.subscribe(errorMessage => {
      if (errorMessage && errorMessage.length > 1) {
        this.loginStore.updateProgressBarVisibility(false);
      }
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * retrieves all data from the store
   */
  getDataFromStore(): void {
    this.loginLoading$ = this.loginStore.LoginLoading$;
    this.loginErrorMessage$ = this.loginStore.Message$;
    this.actualProgress$ = this.loginStore.LoadingProgress$;
    this.maxProgress$ = this.loginStore.MaxProgress$;
    this.showProgressBar$ = this.loginStore.ShowProgressBar$;
    this.loginDelay$ = this.loginStore.Delay$;
  }

  /**
   * on submit click event
   */
  onSubmit(): void {
    if (this.loginStore.ShowProgressBar$.getValue()) {
      return;
    }
    this.loginStore.updateProgressBarVisibility(true);
    this.loginStore.updateLoadingProgress(0);
    this.loginStore.loginUser(this.dynForm.form.get(this.emailFormKey).value, this.dynForm.form.get(this.passwordFormKey).value);
  }

  globalLogout(): void {
    this.activeUsersStore.globalLogoutUser(
      this.dynForm.form.get(this.emailFormKey).value,
      this.dynForm.form.get(this.passwordFormKey).value);
  }
}
